import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SyncResult, SyncResultProjectMetaData } from '../../../models/integration.model';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from '../../../../environments/environment';

/**
 * Sync Results Modal Component
 * Displays detailed results of project synchronization operations
 * Following existing modal patterns in the codebase
 */
@Component({
  selector: 'app-sync-results-modal',
  templateUrl: './sync-results-modal.component.html',
  styleUrls: ['./sync-results-modal.component.scss'],
})
export class SyncResultsModalComponent implements OnInit {
  // Public properties
  syncResult: SyncResult;

  constructor(
    public dialogRef: MatDialogRef<SyncResultsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { syncResult: SyncResult },
    private transloco: TranslocoService,
  ) {
    this.syncResult = data.syncResult;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    // Initialize component - no specific initialization needed
    console.log('Sync Results Modal initialized with result:', this.syncResult);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Close the modal
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * Close modal and refresh projects
   */
  onRefreshProjects(): void {
    this.dialogRef.close(true);
  }

  /**
   * Check if sync has failures
   */
  hasFailures(): boolean {
    return (this.syncResult.data?.failures?.length || 0) > 0;
  }

  /**
   * Check if sync was successful
   */
  isSuccessful(): boolean {
    return this.syncResult.status === 'success';
  }

  /**
   * Get total projects processed
   */
  getTotalProcessed(): number {
    if (!this.syncResult.data) return 0;

    return (
      (this.syncResult.data.projects_created || 0) +
      (this.syncResult.data.projects_updated || 0) +
      (this.syncResult.data.projects_matched || 0)
    );
  }

  /**
   * Get success rate as percentage
   */
  getSuccessRate(): number {
    if (!this.syncResult.data) return 0;

    const total = this.getTotalProcessed() + (this.syncResult.data.projects_failed || 0);
    if (total === 0) return 0;

    return Math.round((this.getTotalProcessed() / total) * 100);
  }

  /**
   * Get formatted duration
   */
  getFormattedDuration(): string {
    if (!this.syncResult.meta_data?.duration_ms) return 'N/A';

    const ms = this.syncResult.meta_data.duration_ms;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }

  /**
   * Get current environment for display
   */
  getCurrentEnvironment(): string {
    if (this.syncResult.meta_data?.environment) {
      return this.syncResult.meta_data.environment;
    }

    // Fallback to environment configuration
    if (environment.production) {
      return 'production';
    } else if (environment.env === 'staging' || environment.env === 'pre-prod') {
      return 'uat';
    } else {
      return 'uat'; // Default for local development
    }
  }

  /**
   * Check if sync result has project IDs in meta_data
   */
  hasProjectIds(): boolean {
    return !!this.syncResult.data?.meta_data;
  }

  /**
   * Get project IDs for a specific category
   */
  getProjectIds(
    category: 'created' | 'updated' | 'matched' | 'failed',
  ): SyncResultProjectMetaData[] {
    if (!this.syncResult.data?.meta_data) return [];

    switch (category) {
      case 'created':
        return this.syncResult.data.meta_data.projects_created || [];
      case 'updated':
        return this.syncResult.data.meta_data.projects_updated || [];
      case 'matched':
        return this.syncResult.data.meta_data.projects_matched || [];
      case 'failed':
        return this.syncResult.data.meta_data.projects_failed || [];
      default:
        return [];
    }
  }
}
